import { Box, Stack, Theme, Typography } from '@mui/material'
import { MouseEvent, SyntheticEvent, useCallback, useRef, useState } from 'react'
import { FiPlus } from 'react-icons/fi'
import { RxChevronDown } from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import { useCreateTableTabMutation } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedTableColumn, DetailedTableTab } from '~/components/tables/types'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'
import { TabSvg } from '../../ui/atoms/tabs/TabSvg'
import { useDossSnackbar } from '../DossSnackbar'
import { SelectTableTabMenu } from './SelectTableTabMenu'
import { TableTabMenu } from './TableTabMenu'

interface TableTabsProps {
  tableId: string
  tableName: string
  tableColumns: DetailedTableColumn[]
  orderedTableTabs: DetailedTableTab[]
  selectedTab: DetailedTableTab | null
  setOrderedTableTabs: (tableTabs: DetailedTableTab[]) => void
  handleTabChange: (tableTab: DetailedTableTab) => void
  tabCountLimit: number
  setOrderedTablesAfterTabCreation: (tableTab: DetailedTableTab) => void
  readOnly: boolean
  hideTabButtons?: boolean
}

export const TEXT_LENGTH_LIMIT = 21
export const POPPER_Z_INDEX = 999

function getStyles(theme: Theme) {
  return {
    tabsContainer: {
      flexGrow: 1,
      alignItems: 'flex-end',
      transition: 'all 0.3s ease',
    },
    tabs: {
      marginInlineStart: theme.spacing(2),
      display: 'flex',
      alignItems: 'flex-end',
      marginBottom: '-1px',
      '& > *:not(:first-of-type)': {
        marginInlineStart: theme.spacing(-2),
      },
    },
    tab: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    menuItem: {
      width: 180,
    },
    popperZIndex: {
      zIndex: POPPER_Z_INDEX,
    },
    addTab: {
      marginInlineStart: theme.spacing(-0.5),
      display: 'flex',
      paddingTop: theme.spacing(2),
      paddingInlineEnd: theme.spacing(2.5),
      paddingBottom: theme.spacing(1),
      paddingInlineStart: theme.spacing(0),
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    selectMenuButton: {
      height: '48px',
      width: '48px',
      display: 'flex',
      marginInlineStart: theme.spacing(-0.5),
      paddingTop: theme.spacing(2),
      paddingInlineEnd: theme.spacing(2.5),
      paddingBottom: theme.spacing(1),
      paddingInlineStart: theme.spacing(0),
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
    },
  }
}

const getTableTabNameDisplay = (tableTab: DetailedTableTab) => {
  return tableTab.name.length > TEXT_LENGTH_LIMIT
    ? `${tableTab.name.slice(0, TEXT_LENGTH_LIMIT)}...`
    : tableTab.name
}

/**
 * Renders table tabs for table
 */
export function TableTabs({
  tableId,
  tableName,
  orderedTableTabs,
  tableColumns,
  selectedTab,
  setOrderedTableTabs,
  handleTabChange,
  tabCountLimit,
  setOrderedTablesAfterTabCreation,
  readOnly,
  hideTabButtons = false,
}: TableTabsProps) {
  const { t } = useTypedTranslation()
  const styles = useStyles(getStyles)
  const snackbar = useDossSnackbar()
  const { permissionsManager } = useGlobalContext()

  const anchorRef = useRef<HTMLButtonElement>(null)
  const [tableTabAnchorEl, setTableTabAnchorEl] = useState<null | HTMLElement>(null)
  const [additionalTabsDropdownOpen, setAdditionalTabsDropdownOpen] = useState(false)

  const canConfigureTableTabs = permissionsManager.canConfigureTableTabs(tableId)

  const [createTableTab] = useCreateTableTabMutation()

  const handleAddNewTableTabClick = useCallback(
    async (event?: React.MouseEvent) => {
      event?.stopPropagation()
      // Create a new table using the current date time, next possible order,
      // no filters, no sorts, all table columns
      const columnInputs = tableColumns.map((tableColumn) => ({
        tableColumnId: tableColumn.id,
        order: tableColumn.order,
      }))
      await createTableTab({
        variables: {
          input: {
            tableId,
            columns: columnInputs,
            filters: [],
            sorts: [],
          },
        },
      })
        .then(({ data }) => {
          if (data) {
            snackbar.showInfo(
              t('table_tab.create.success', { newTabName: data.createTableTab.name }),
            )
            setOrderedTablesAfterTabCreation(data.createTableTab)
          }
        })
        .catch(() => {
          snackbar.showError(t('table_tab.create.error'))
        })
    },
    [tableColumns, tableId, createTableTab, snackbar, setOrderedTablesAfterTabCreation, t],
  )

  if (!selectedTab) {
    return null
  }

  const handleTabClick = (event: MouseEvent<HTMLElement>, tableTabIdValue: string) => {
    if (readOnly) {
      return
    }
    event.stopPropagation()
    // If user clicks on already selected tab, open the tab menu
    if (selectedTab.id === tableTabIdValue) {
      setTableTabAnchorEl(event.currentTarget)
    }
  }

  const handleTabMenuClose = (event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation()
    setTableTabAnchorEl(null)
  }

  const handleTabSelection = (event: SyntheticEvent, selectedTabIdValue: string) => {
    event.stopPropagation()
    event.preventDefault()
    const newTab = orderedTableTabs.find((tab) => tab.id === selectedTabIdValue)
    if (newTab && newTab.id !== selectedTab.id) {
      handleTabChange(newTab)
    }
    handleTabMenuClose()
  }

  const handleAdditionalTabsDropdownToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setAdditionalTabsDropdownOpen(!additionalTabsDropdownOpen)
    handleTabMenuClose(event)
  }

  const setAdditionalTabsDropdownClose = (event: SyntheticEvent | Event) => {
    event.stopPropagation()
    setAdditionalTabsDropdownOpen(false)
  }

  return (
    <Stack className="table-tabs-container" direction="row" sx={styles.tabsContainer}>
      <Stack className="table-tabs" direction="row" sx={styles.tabs}>
        {orderedTableTabs
          .filter((tab) => (hideTabButtons ? tab.id === selectedTab.id : true))
          .slice(0, hideTabButtons ? 1 : tabCountLimit)
          .map((tableTab) => (
            <Tooltip key={tableTab.name} arrow offset={-10} placement="top" title={tableTab.name}>
              <TabSvg
                key={tableTab.id}
                handleClick={(event: MouseEvent<HTMLElement>) => {
                  handleTabSelection(event, tableTab.id)
                  handleTabClick(event, tableTab.id)
                }}
                selected={selectedTab.id === tableTab.id}
                showChevron={!readOnly && canConfigureTableTabs}
                value={tableTab.id}
                variant="primary"
              >
                <Typography className="tabTitle" variant="body2">
                  {getTableTabNameDisplay(tableTab)}
                </Typography>
              </TabSvg>
            </Tooltip>
          ))}
      </Stack>
      {canConfigureTableTabs && (
        <TableTabMenu
          anchorEl={tableTabAnchorEl}
          handleTabChange={handleTabChange}
          handleTabMenuClose={handleTabMenuClose}
          orderedTableTabs={orderedTableTabs}
          selectedTab={selectedTab}
          tableId={tableId}
          tableName={tableName}
        />
      )}
      <If condition={orderedTableTabs.length > tabCountLimit && !hideTabButtons}>
        <Then>
          <Box ref={anchorRef} sx={styles.selectMenuButton}>
            <IconButton
              className="table-toolbar-actions"
              onClick={handleAdditionalTabsDropdownToggle}
              size="small"
              variant={'true-ghost'}
            >
              <RxChevronDown />
            </IconButton>
          </Box>
          <SelectTableTabMenu
            anchorEl={anchorRef.current}
            canConfigureTableTabs={canConfigureTableTabs}
            getTableTabNameDisplay={getTableTabNameDisplay}
            handleAddNewTableTabClick={handleAddNewTableTabClick}
            handleDropdownClose={setAdditionalTabsDropdownClose}
            handleTabChange={handleTabChange}
            handleTabMenuClose={handleTabMenuClose}
            open={additionalTabsDropdownOpen}
            orderedTableTabs={orderedTableTabs}
            readOnly={readOnly}
            setOrderedTableTabs={setOrderedTableTabs}
            tabCountLimit={tabCountLimit}
            textLengthLimit={TEXT_LENGTH_LIMIT}
          />
        </Then>
        <Else>
          {!readOnly && canConfigureTableTabs && (
            <Tooltip
              arrow
              offset={-25}
              placement="right"
              title={t('table_tab.create.add_icon_tooltip')}
            >
              <Box ref={anchorRef} sx={styles.addTab}>
                <IconButton
                  onClick={handleAddNewTableTabClick}
                  size="medium"
                  variant={'true-ghost'}
                >
                  <FiPlus />
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Else>
      </If>
    </Stack>
  )
}
